import type { PivotColumn } from './PivotTable';

/**
 * The Daily View lines in the workbook's order. Cash, credit, promo and airline, points and
 * outstanding add up to gross sales on sale rows; the refund lines split customer refunds by how
 * they were paid back.
 */
export const DAILY_LINES: ReadonlyArray<PivotColumn> = [
  { key: 'gross_sales_aud', label: 'Gross sales', format: 'currency' },
  { key: 'refunds_aud', label: 'Accounting refund', format: 'currency' },
  { key: 'cash_sales_aud', label: 'Cash sales', format: 'currency' },
  { key: 'cash_refunds_aud', label: 'Cash refunds', format: 'currency' },
  { key: 'credit_sales_aud', label: 'Credit sales', format: 'currency' },
  { key: 'credit_refunds_aud', label: 'Credit refunds', format: 'currency' },
  { key: 'promo_airline_sales_aud', label: 'Promo & airline used', format: 'currency' },
  { key: 'promo_airline_refunds_aud', label: 'Promo & airline refunds', format: 'currency' },
  // Sale rows only, so the lines add up to gross; points_sales_aud also counts refund rows and is the fallback.
  { key: 'points_sale_rows_aud', label: 'Loyalty benefits & points sales', format: 'currency' },
  { key: 'points_sales_aud', label: 'Loyalty benefits & points sales', format: 'currency' },
  { key: 'points_refunds_aud', label: 'Loyalty benefits & points refunds', format: 'currency' },
  { key: 'outstanding_sales_aud', label: 'Outstanding actual sales', format: 'currency' },
  // The combined promo, voucher and airline points line, for responses without the split above.
  { key: 'other_sales_aud', label: 'Promo, voucher, airline pts', format: 'currency' },
  { key: 'net_sales_aud', label: 'Net sales', format: 'currency' },
  { key: 'net_margin_aud', label: 'Gross margin (AR)', format: 'currency' },
];

/** The Monthly View payment lines, next to its existing columns. */
export const MONTHLY_LINES: ReadonlyArray<PivotColumn> = [
  { key: 'cash_sales_aud', label: 'New cash sales', format: 'currency' },
  { key: 'credit_sales_aud', label: 'Credit sales', format: 'currency' },
  { key: 'cash_refunds_aud', label: 'Cash refunds', format: 'currency' },
  { key: 'credit_refunds_aud', label: 'Credit refunds', format: 'currency' },
];

/** The lines that add up to gross sales on sale rows. */
export const GROSS_PARTS = [
  'cash_sales_aud',
  'credit_sales_aud',
  'promo_airline_sales_aud',
  'points_sale_rows_aud',
  'outstanding_sales_aud',
] as const;

/** Lines svc-reporting added with the payment split; older responses do not carry them. */
const SPLIT_LINES = new Set([
  'promo_airline_sales_aud',
  'points_sale_rows_aud',
  'outstanding_sales_aud',
  'cash_refunds_aud',
  'credit_refunds_aud',
  'promo_airline_refunds_aud',
  'points_refunds_aud',
]);

const returned = (rows: ReadonlyArray<Record<string, unknown>>, key: string) =>
  rows.some((row) => row[key] !== undefined);

/**
 * Only the lines the response carries, so the page works before and after svc-reporting returns the
 * split. Once promo and airline is split out, the combined line would count it twice, so it goes; once the
 * sale-row points line arrives, the all-rows points line goes for the same reason.
 */
export function returnedLines(
  lines: ReadonlyArray<PivotColumn>,
  rows: ReadonlyArray<Record<string, unknown>>,
): Array<PivotColumn> {
  if (!rows.length) {
    // Nothing to read presence from (no rows in the window): keep the headers every response has.
    return lines.filter((line) => !SPLIT_LINES.has(line.key));
  }
  const split = returned(rows, 'promo_airline_sales_aud');
  const saleRowPoints = returned(rows, 'points_sale_rows_aud');
  return lines.filter(
    (line) =>
      returned(rows, line.key) &&
      !(split && line.key === 'other_sales_aud') &&
      !(saleRowPoints && line.key === 'points_sales_aud'),
  );
}
